import React, { useState, useEffect, useMemo } from "react";
import KingOfTheMountain from "./KingOfTheMountain";
import PhotoMountain from "./PhotoMountain";

import { UseUserDetails, UseIsDemo, UseIsAdmin } from "../../hooks/auth";
import {
  UseCompany,
  UseYetiStats,
  UseSelectedWorkspace,
} from "../../hooks/company";
import { UseMyKpis, UseTopPlayers, UseGroupKpis, UseGroupAllKpis, useGetPreviousKpis } from "../../hooks/kpi";
import { recomputeMonthTotal } from "../../helpers/kpiPoints";
import { lastMonthMoment, recomputeClosedMonth } from "../../helpers/historicMonthBoard";

// periodIdx → the score-bucket key, mirroring the dashboard filter order.
const IDX_TO_BUCKET = { 0: "yesterday", 1: "today", 2: "week", 4: "lastWeek", 3: "month", 5: "lastMonth" };

const Gameboard = ({ type, mode, playerScore, yetiScore, maxPoint, darkMode, noScroll, tvTrophyOverlay, peakTrophy, title, dashboard, periodIdx = 3 }) => {
  const [gameboard, setGameboard] = useState(null);
  const [settings, setSettings] = useState(null);

  const user = UseUserDetails();

  const isDemo = UseIsDemo();
  const yetiStats = UseYetiStats();
  const isAdmin = UseIsAdmin();
  const workspace = UseSelectedWorkspace();
  const { logo, businessName } = UseCompany();
  const userKpi = UseMyKpis();
  const groupKpis = UseGroupKpis();
  const groupAllKpis = UseGroupAllKpis();
  const bucket = IDX_TO_BUCKET[periodIdx] || "month";
  const lastMonthRef = useMemo(() => lastMonthMoment(), []);
  const { getPreviousKpis } = useGetPreviousKpis();
  const catalogReady = !!(groupKpis?.length || groupAllKpis?.length);
  const topPlayers = bucket === "lastMonth"
    ? UseTopPlayers("month", lastMonthRef)
    : UseTopPlayers(bucket);

  useEffect(() => {
    if (bucket === "lastMonth" && catalogReady) getPreviousKpis(lastMonthRef, true);
  }, [bucket, lastMonthRef, getPreviousKpis, catalogReady]);
  // Last Month scores against the same workspace KPI set the live board uses,
  // so King of the Mountain stays consistent with This Month for the workspace.
  const closedMonthScoring = groupKpis;
  const yetiBucketKey = bucket === "lastMonth" ? "month" : bucket;
  // Admin view: average across ALL active non-Yeti scored players. (A stale
  // slice(1) here used to drop the top player — it predated the isYeti filter,
  // when position 0 was assumed to be the Yeti.)
  const activePlayers = topPlayers?.filter(
    (player) => player.isActive !== false && !player.isYeti,
  ) ?? [];
  // The average only counts players who actually scored in the selected
  // period — on Today/Yesterday/Week a roster of non-scorers used to drag
  // the team dot to the ground. NaN still means "roster not loaded yet"
  // (the effect below waits on it); an all-zero day is a real 0.
  const playerTotals = activePlayers.map((player) => {
    if (bucket === "lastMonth") {
      return Number(recomputeClosedMonth(player.scores, closedMonthScoring).total) || 0;
    }
    return recomputeMonthTotal(player.scores, groupKpis);
  });
  const scoredTotals = playerTotals.filter((t) => t !== 0);
  let averageScore = NaN;
  if (activePlayers.length) {
    averageScore = scoredTotals.length
      ? scoredTotals.reduce((sum, t) => sum + t, 0) / scoredTotals.length
      : 0;
  }

  useEffect(() => {
    if (!user) return;
    /* Last Month waits for folded historic rows — don't freeze the climb on
       a prior period's settings while that fetch is in flight. */
    if (bucket === "lastMonth" && !Array.isArray(topPlayers)) return;
    /* Head of Sales (and other empty hosts) have no groupKpis → UseYetiStats
       stays null forever. ScopedGameboard already settled inherited team /
       yeti points — do not require host yetiStats when those overrides exist. */
    const hasScopedOverride = playerScore != null;
    if (!yetiStats && !hasScopedOverride) return;
    /* Scoped / cross-workspace cards hand in playerScore (incl. 0). They must
       not wait on this board's selected workspace roster — Head of Sales has a
       workspace doc but zero players, so averageScore stays NaN and the old
       `!workspace` / NaN guard blanked the mountain forever. */
    if (playerScore == null && !workspace) return;
    /* `averageScore` is the admin's OWN average, computed from this workspace's
       loaded players. When a scoped score is handed in (a scoped card climbing
       another workspace, or a combined cross-workspace average) it is not used
       at all — so bailing out on it left the board sitting at zero with
       perfectly good numbers in hand. Only guard it when we compute it. */
    if (isAdmin && playerScore == null && Number.isNaN(averageScore)) return;

    const boardSettings = {};

    const yetiBucketTotal = yetiStats?.[yetiBucketKey]?.total ?? yetiStats?.month?.total ?? 0;
    boardSettings.yetiScore = yetiScore ?? yetiBucketTotal;

    const bucketPlayerScore = playerScore ?? (isAdmin
      ? averageScore
      : recomputeMonthTotal(userKpi?.[bucket], groupKpis));

    // The month keeps its true ceiling (the yeti's best possible month). A
    // shorter window scales the mountain to its own contenders instead — the
    // monthly ceiling would pin both dots to the foot of the climb.
    if (bucket === "month" || bucket === "lastMonth") {
      boardSettings.maxPoint =
        maxPoint ?? (yetiStats?.maxTotal || 10000) + (isDemo ? 500 : 0);
    } else {
      boardSettings.maxPoint =
        maxPoint ?? Math.max(yetiBucketTotal, bucketPlayerScore, 1) * 1.25;
    }

    boardSettings.playerScore = bucketPlayerScore;

    if (mode === "company") {
      boardSettings.playerName = businessName;
      boardSettings.playerLogo = logo;
    } else if (isAdmin) {
      boardSettings.playerName = workspace.name;
      boardSettings.playerLogo = workspace.image;
    } else {
      boardSettings.playerName = user.firstName;
      boardSettings.playerLogo = user.image;
    }
    setSettings(boardSettings);
  }, [mode, userKpi, yetiScore, playerScore, maxPoint, yetiStats, isAdmin, user, workspace, isDemo, businessName, logo, averageScore, groupKpis, bucket, topPlayers]);

  useEffect(() => {
    if (!settings) return;
    switch (type) {
      case "photo":
        setGameboard(<PhotoMountain {...settings} darkMode={darkMode} noScroll={noScroll} peakTrophy={peakTrophy} cardTitle={title} dashboard={dashboard} />);
        break;
      default:
        setGameboard(<KingOfTheMountain {...settings} darkMode={darkMode} noScroll={noScroll} tvTrophyOverlay={tvTrophyOverlay} peakTrophy={peakTrophy} cardTitle={title} dashboard={dashboard} />);
    }
  }, [type, settings, darkMode, noScroll, tvTrophyOverlay, peakTrophy, title, dashboard]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{gameboard}</>;
};

export default Gameboard;
